import { inject, provideAppInitializer } from '@angular/core';
import { Store } from '@ngrx/store';
import { LOGGER, Logger } from '../shared/logger';
import { PERSISTED_SLICES, PersistedSlice } from './persisted-state';
import { STATE_STORAGE, StateStorage } from './state-storage';
import { StorageActions } from './storage.actions';

/**
 * Restores every registered slice before the first view renders, so no component ever
 * observes pre-hydration state and nothing has to guard on "already hydrated?".
 *
 * Blocking the start is affordable because this only touches local storage; it needs no
 * token and no network, and runs alongside the OIDC initializer.
 */
export const provideStorageInit = () =>
  provideAppInitializer(() => {
    const storage = inject(STATE_STORAGE);
    const slices = inject(PERSISTED_SLICES);
    const store = inject(Store);
    const log = inject(LOGGER).scope('storage');
    return hydrateAll(storage, slices, store, log);
  });

/**
 * Never rejects, and a slice that cannot be read does not take the others down with it —
 * a single broken payload must not cost the app its whole state, let alone its start.
 */
const hydrateAll = async (
  storage: StateStorage,
  slices: readonly PersistedSlice<unknown>[],
  store: Store,
  log: Logger,
): Promise<void> => {
  const loaded: Record<string, unknown> = {};

  await Promise.all(
    slices.map(async slice => {
      try {
        const stored = await storage.load<unknown>(slice.name);
        if (stored !== null) loaded[slice.name] = stored;
      } catch (e: unknown) {
        log.error('read failed, slice falls back to defaults', {
          key: slice.name,
          error: String(e),
        });
      }
    }),
  );

  log.info('hydrating', {
    registered: slices.map(slice => slice.name),
    fromStorage: Object.keys(loaded),
  });

  // Dispatched in every case — it is also the signal that writing back may begin.
  store.dispatch(StorageActions.hydrated({ slices: loaded }));
};
